import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import Highcharts from "highcharts"; // to get highchart functions
import SwitchTabs from "components/common/SwitchTabs";
import HighchartWrapper from "components/common/HighchartWrapper";
import {
  PRICE_TYPE,
  getPriceType,
} from "components/ops-mgmt/account/upr-segmentation/UPRSegmentationHelper";

import { getDateTimeXAxis } from "helpers/Chart";

import {
  GROUP_TYPE_INDEX,
  getSegmentPriceSectionData,
  getPausedSection,
} from "./AnatomyCstReportHelper";
import {
  getGroupTypeColor,
  getGroupTypeSeriesOptions,
  tooltipFormatter,
  parsePausedPlotBands,
} from "./AnatomyHighchartHelper";
import { AnatomyCSTContext } from "./context/AnatomyCSTContext";
import {
  useAnatomyFilter,
  useAnatomyData,
  useAnatomyUI,
} from "./context/AnatomyContexts";

const SELECT = {
  ALL: "All",
  HIDE_NO_UPR: "Hide No UPR",
  FLOOR_ONLY: "Floor Only",
  CPM_ONLY: "CPM Only",
};

const titleClass = "block text-2xl text-gray-800 font-bold mb-1 mt-2";
class AnatomyCstPriceSection extends React.Component {
  static contextType = AnatomyCSTContext;

  constructor(props) {
    super(props);

    this.ref1 = React.createRef();
    this.ref2 = React.createRef();
    this.ref3 = React.createRef();

    this.state = {
      options: null,
      optionsImp: null,
      optionsReq: null,
      tabs: _.keys(SELECT).map((k) => ({
        value: SELECT[k],
        label: SELECT[k],
      })),
      selectedTabs: SELECT.ALL,
      isShowOBX: true,
    };

    this.clearData = this.clearData.bind(this);
    this.updateData = this.updateData.bind(this);
    this.redrawCharts = this.redrawCharts.bind(this);
    this.setExtremes = this.setExtremes.bind(this);
    this.toggleOBX = this.toggleOBX.bind(this);
  }

  componentDidMount() {
    this.updateData();
    this.toggleOBX();
  }

  componentDidUpdate(prevProps, prevState) {
    // console.log(prevProps.uprSegmentId, this.props.uprSegmentId);
    if (prevProps.uprSegmentId !== this.props.uprSegmentId) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.dateRangeNum, this.props.dateRangeNum);
    if (prevProps.dateRangeNum !== this.props.dateRangeNum) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.aggregation, this.props.aggregation);
    if (prevProps.aggregation !== this.props.aggregation) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.includeToday, this.props.includeToday);
    if (prevProps.includeToday !== this.props.includeToday) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.isIncludeGhostImp, this.props.isIncludeGhostImp);
    if (prevProps.isIncludeGhostImp !== this.props.isIncludeGhostImp) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.noPbType, this.props.noPbType);
    if (prevProps.noPbType !== this.props.noPbType) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.demandTypes, this.props.demandTypes);
    if (prevProps.demandTypes !== this.props.demandTypes) {
      this.refreshData();
      this.redrawCharts();
    }

    // console.log(prevProps.selectedGroupTypes, this.props.selectedGroupTypes);
    if (prevProps.selectedGroupTypes !== this.props.selectedGroupTypes) {
      this.redrawCharts();
    }

    // console.log(prevProps.timezoneType, this.props.timezoneType);
    if (prevProps.timezoneType !== this.props.timezoneType) {
      this.updateData();
      this.redrawCharts();
    }

    // set extremes
    if (prevProps.st !== this.props.st || prevProps.et !== this.props.et) {
      this.setExtremes({ st: this.props.st, et: this.props.et });
    }

    if (prevState.selectedTabs !== this.state.selectedTabs) {
      this.updateData();
      this.redrawCharts();
    }
  }

  setExtremes({ st, et }) {
    // The better zoom! Update min max on xAxis, instead of setExtremes
    // when min, max is null, resets xAxis
    for (let i = 1; i <= 3; ++i) {
      const chart = this[`ref${i}`].current.ref.current.chart;
      chart.xAxis[0].update({ min: st, max: et });

      // hide/show reset zoom button
      if (!st && !et) {
        if (chart.resetZoomButton) {
          chart.resetZoomButton.hide();
        }
      } else {
        if (chart.resetZoomButton) {
          chart.resetZoomButton.show();
        } else {
          chart.showResetZoom();
        }
      }
    }
  }

  refreshData() {
    const cb = this.updateData;
    this.clearData(cb);
  }

  clearData(cb) {
    // console.log("clear data");
    this.setState(
      {
        options: null, // price chart is not affected, but to clear zoom clear this anyway
        optionsImp: null,
        optionsReq: null,
      },
      cb
    );
  }

  updateData() {
    const { data, demandTypes, timezoneType } = this.props;
    const { timezone } = this.context;
    const { selectedTabs } = this.state;

    const priceFilter = this._getPriceFilter(selectedTabs);
    // console.log("update data", uprSegmentId, data);

    const { priceSectionData, layer1Data } = getSegmentPriceSectionData({
      reports: data,
      demandTypes,
    });

    let tz = timezone;
    if (timezoneType === "TAIPEI") {
      tz = "Asia/Taipei";
    }

    const pausedSection = getPausedSection(priceSectionData, tz);

    const setExtremeFn = this.props.handleSetExtremes;
    const priceSectionDataFiltered = _.mapValues(priceSectionData, (list) => {
      return _.filter(list, (d) => {
        if (priceFilter.length === 0) {
          return true;
        }

        const { type } = getPriceType(d.price);
        if (_.indexOf(priceFilter, type) !== -1) {
          return true;
        }
        return false;
      });
    });

    const options = _createPriceChartOptions(
      priceSectionDataFiltered,
      pausedSection,
      tz,
      setExtremeFn
    );
    const optionsImp = _createImpChartOptions(
      layer1Data,
      pausedSection,
      tz,
      setExtremeFn
    );
    const optionsReq = _createReqChartOptions(
      layer1Data,
      pausedSection,
      tz,
      setExtremeFn
    );

    this.setState({
      options,
      optionsImp,
      optionsReq,
      timezone: tz,
    });
  }

  redrawCharts() {
    const { selectedGroupTypes, uprSegmentId } = this.props;
    const { isShowOBX } = this.state;

    setTimeout(() => {
      // console.log(this.ref1, this.ref1.current.ref.current.chart);
      // console.log(this.ref2, this.ref2.current.ref.current.chart);
      // console.log(this.ref3, this.ref3.current.ref.current.chart);

      const trendCharts = [
        ...(uprSegmentId !== -1 ? [this.ref1.current.ref.current.chart] : []),
        this.ref2.current.ref.current.chart,
        this.ref3.current.ref.current.chart,
      ];

      trendCharts.forEach((chart, idx) => {
        const series = chart.series;

        for (let s of series) {
          if (idx !== 0 && _.indexOf(["o", "b", "x"], s.name) !== -1) {
            if (isShowOBX) {
              s.show();
            } else {
              s.hide();
            }
          } else if (_.indexOf(selectedGroupTypes, s.name) !== -1) {
            s.show();
          } else {
            s.hide();
          }
        }
      });
    }, 100);
  }

  toggleOBX() {
    const { isShowOBX } = this.state;
    this.setState({ isShowOBX: !isShowOBX });

    setTimeout(() => {
      this.redrawCharts();
    }, 100);
  }

  _getPriceFilter(selected) {
    switch (selected) {
      case SELECT.HIDE_NO_UPR:
        return [
          PRICE_TYPE.ALGO,
          PRICE_TYPE.TARGET_CPM,
          PRICE_TYPE.FLOOR,
          PRICE_TYPE.LGO,
        ];
      case SELECT.FLOOR_ONLY:
        return [PRICE_TYPE.FLOOR];
      case SELECT.CPM_ONLY:
        return [PRICE_TYPE.TARGET_CPM];
      case SELECT.ALL:
      default:
        return [];
    }
  }

  render() {
    const { options, optionsImp, optionsReq, tabs, selectedTabs, isShowOBX } =
      this.state;

    const { uprSegmentId } = this.props;
    // console.log("optionsImp", optionsImp);
    // console.log("optionsReq", optionsReq);
    // console.log("options", options);
    return (
      <div>
        <div className="flex items-center justify-between">
          <div className={titleClass}>Price and Data Integrity</div>
        </div>

        <div className="flex justify-between bg-white">
          <div className="flex w-1/3 justify-end px-2 pt-2">
            {uprSegmentId !== -1 && (
              <SwitchTabs
                tabs={tabs}
                selectedTabKey={selectedTabs}
                handleChange={(tab) =>
                  this.setState({
                    selectedTabs: tab,
                  })
                }
              ></SwitchTabs>
            )}
          </div>
          <div className="px-3 pt-2">
            <button
              type="button"
              className="rounded border border-gray-400 bg-gray-100 px-2 font-semibold text-gray-800 hover:border-gray-500 hover:text-gray-900"
              onClick={this.toggleOBX}
            >
              {isShowOBX ? "Hide" : "Show"} o, b, x in Imp and Req
            </button>
          </div>
        </div>

        <div className="flex items-center bg-white">
          <div className="w-1/3">
            {options && uprSegmentId !== -1 && (
              <HighchartWrapper
                ref={this.ref1}
                options={options}
              ></HighchartWrapper>
            )}
            {uprSegmentId === -1 && (
              <div className="p-4  " style={{ height: "400px" }}>
                <div className="border rounded flex h-full w-full flex-col items-center justify-center border-dashed border-gray-400 bg-gray-100">
                  <div className="text-center text-gray-600">
                    Not available.
                  </div>
                  <div className="text-center text-gray-600">
                    Please select a valid UPR Segment to view Price Chart.
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="w-1/3">
            {optionsImp && (
              <HighchartWrapper
                ref={this.ref2}
                options={optionsImp}
              ></HighchartWrapper>
            )}
          </div>
          <div className="w-1/3">
            {optionsReq && (
              <HighchartWrapper
                ref={this.ref3}
                options={optionsReq}
              ></HighchartWrapper>
            )}
          </div>
        </div>
        {/* <div className="flex items-center">
          <div className="w-1/3"></div>
          <div className="w-1/3"></div>
          <div className="w-1/3">
            {optionsReq && (
              <HighchartWrapper
                ref={this.ref3}
                options={optionsReq}
                timezone={this.state.timezone}
              ></HighchartWrapper>
            )}
          </div>
        </div> */}
      </div>
    );
  }
}

function _createPriceSeriesData(reportsByGroupType, pausedSection, timezone) {
  const groupTypes = _.keys(reportsByGroupType);
  const numOfDetectors = _.sumBy(groupTypes, (gt) => {
    return _.startsWith(gt, "d") ? 1 : 0;
  });
  const seriesData = _.map(groupTypes, (gt) => {
    const reports = reportsByGroupType[gt];
    const sortedReports = _.sortBy(reports, "time");
    const data = _.map(sortedReports, (r) => {
      const { type, value } = getPriceType(r.price);
      const x =
        r.time * 1000 +
        moment.tz(r.time * 1000, timezone).utcOffset() * 60 * 1000;
      const y = type === PRICE_TYPE.TARGET_CPM ? value : r.price;
      return {
        x,
        y: _.find(
          pausedSection,
          (section) => section.from <= x && section.to >= x
        )
          ? null
          : y,
        priceType: type,
      };
    });

    const zones = [];
    if (data.length > 0) {
      let currentType = data[0].priceType;
      // Dash for PRICE_TYPE.TARGET_CPM
      // Solid for other PRICE_TYPE
      _.forEach(data, (d) => {
        if (
          d.priceType !== currentType &&
          (d.priceType === PRICE_TYPE.TARGET_CPM ||
            currentType === PRICE_TYPE.TARGET_CPM)
        ) {
          zones.push({
            value: d.x - 30 * 60 * 1000,
            dashStyle: currentType === PRICE_TYPE.TARGET_CPM ? "Dash" : "Solid",
          });
          currentType = d.priceType;
        }
      });

      if (data[data.length - 1].priceType === currentType) {
        zones.push({
          value: data[data.length - 1].x,
          dashStyle: currentType === PRICE_TYPE.TARGET_CPM ? "Dash" : "Solid",
        });
      }
    }

    const color = getGroupTypeColor(gt, numOfDetectors);
    const d = {
      name: gt,
      type: "line",
      data,
      color,
      zoneAxis: "x",
      zones,
      ...getGroupTypeSeriesOptions(gt),
    };

    return d;
  });

  return _.sortBy(seriesData, (d) => {
    const gt = d.name;
    return GROUP_TYPE_INDEX[gt];
  });
}

function _createPriceChartOptions(
  reportsByGroupType,
  pausedSection,
  timezone,
  setExtremeFn
) {
  const seriesData = _createPriceSeriesData(
    reportsByGroupType,
    pausedSection,
    timezone
  );
  const options = {
    title: {
      text: "Price",
    },
    time: {
      useUTC: true,
    },
    chart: {
      zoomType: "x",
      events: {
        selection: (e) => _selectionEventFn(e, setExtremeFn),
      },
    },
    plotOptions: {
      series: {
        step: "left",
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        return tooltipFormatter({
          chart: this,
          timezone,
          valueDecimalPoint: 2,
          priceFormat: true,
        });
      },
    },
    xAxis: {
      ...getDateTimeXAxis({ timezone }),
      plotBands: parsePausedPlotBands(pausedSection),
    },
    yAxis: [
      {
        title: {
          text: "$",
        },
      },
    ],
    series: seriesData,
  };

  return options;
}

function _createImpSeriesData(reportsByGroupType, timezone) {
  const numOfDetectors = _.sumBy(_.keys(reportsByGroupType), (gt) => {
    return _.startsWith(gt, "d") ? 1 : 0;
  });
  const seriesData = _.map(reportsByGroupType, (reports, groupType) => {
    const data = _.map(reports, (r) => {
      return {
        x:
          r._time * 1000 +
          moment.tz(r._time * 1000, timezone).utcOffset() * 60 * 1000,
        y: r._layer1_imp,
      };
    });
    return {
      name: groupType,
      type: "spline",
      marker: {
        enabled: false,
      },
      color: getGroupTypeColor(groupType, numOfDetectors),
      data,
    };
  });

  return _.sortBy(seriesData, (d) => {
    const gt = d.name;
    return GROUP_TYPE_INDEX[gt];
  });
}

function _createImpChartOptions(
  reportsByGroupType,
  pausedSection,
  timezone,
  setExtremeFn
) {
  const seriesData = _createImpSeriesData(reportsByGroupType, timezone);

  const options = {
    title: {
      text: "Impressions (Detector L1)",
    },
    time: {
      useUTC: true,
    },
    chart: {
      zoomType: "x",
      events: {
        selection: (e) => _selectionEventFn(e, setExtremeFn),
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      formatter() {
        return tooltipFormatter({
          chart: this,
          timezone,
        });
      },
    },
    xAxis: {
      ...getDateTimeXAxis({ timezone }),
      plotBands: parsePausedPlotBands(pausedSection),
    },
    yAxis: [
      {
        title: {
          text: "",
        },
      },
    ],
    series: seriesData,
  };

  return options;
}

function _createReqSeriesData(reportsByGroupType, timezone) {
  const numOfDetectors = _.sumBy(_.keys(reportsByGroupType), (gt) => {
    return _.startsWith(gt, "d") ? 1 : 0;
  });

  const d1ReqByDate = _.keyBy(reportsByGroupType["d1"], "_time");

  const seriesData = _.map(reportsByGroupType, (reports, groupType) => {
    const data = _.map(reports, (r) => {
      const d1Req = _.get(d1ReqByDate, [r._time, "layer1_req"]);
      const currentReq = r.layer1_req;
      const dist = currentReq - d1Req;
      const distRatio = _.round((dist / d1Req) * 100, 2);
      return {
        x:
          r._time * 1000 +
          moment.tz(r._time * 1000, timezone).utcOffset() * 60 * 1000,
        y: r.layer1_req,
        d1Dist: dist,
        distRatio,
      };
    });
    return {
      name: groupType,
      type: "spline",
      marker: {
        enabled: false,
      },
      color: getGroupTypeColor(groupType, numOfDetectors),
      data,
    };
  });

  return _.sortBy(seriesData, (d) => {
    const gt = d.name;
    return GROUP_TYPE_INDEX[gt];
  });
}

function _createReqChartOptions(
  reportsByGroupType,
  pausedSection,
  timezone,
  setExtremeFn
) {
  const seriesData = _createReqSeriesData(reportsByGroupType, timezone);

  const options = {
    title: {
      text: "Client Requests",
    },
    time: {
      useUTC: true,
    },
    chart: {
      zoomType: "x",
      events: {
        selection: (e) => _selectionEventFn(e, setExtremeFn),
      },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: "rgba(255,255,255,1)", // use full white to make looking at all those numbers easier to read
      formatter() {
        return _tooltipFormatterReq({
          chart: this,
          timezone,
        });
      },
    },
    xAxis: {
      ...getDateTimeXAxis({ timezone }),
      plotBands: parsePausedPlotBands(pausedSection),
    },
    yAxis: [
      {
        title: {
          text: "",
        },
      },
    ],
    series: seriesData,
  };

  return options;
}

function _selectionEventFn(e, setExtremeFn) {
  e.preventDefault();
  // when reset button clicked
  if (e.resetSelection) {
    setExtremeFn({ min: null, max: null });
  }

  // when dragging zoom on xAxis
  if (e.xAxis) {
    const min = e.xAxis[0].min;
    const max = e.xAxis[0].max;
    // console.log(min, max, new Date(min), new Date(max));
    setExtremeFn({ min, max });
  }
}

// Sort By Y Value!
export function _tooltipFormatterReq({
  chart,
  timezone,
  isSortByYValue = false,
  valuePrefix = "",
  valuePostfix = "",
  valueDecimalPoint = 0,
}) {
  let sortedPoints = chart.points;
  if (isSortByYValue) {
    sortedPoints = chart.points.sort((pointA, pointB) => pointB.y - pointA.y);
  }

  return `
    <div style="text-align: center; font-size: 12px; margin-bottom: 2px;">
      ${moment
        .tz(
          chart.x - moment.tz(chart.x, timezone).utcOffset() * 60 * 1000,
          timezone
        )
        .format("YYYY-MM-DD HH:mm Z")}
    </div>
    
    <div>
      <table style="margin: 0 auto;">
        <tbody>
          <tr>
            <td colspan="5" style="text-align: right;">(distance to d1)</td>
          </tr>
          ${sortedPoints
            .map((point) => {
              const isOB =
                point.series.name === "b" ||
                point.series.name === "o" ||
                point.series.name === "x";
              return `
                <tr>
                  <td>
                    <span style="color: ${point.color};">\u25CF</span>
                  </td>
                  <td style="text-align: right; padding-right: 3px; ${
                    isOB && `font-weight: 600;`
                  }">
                    <span style="${isOB && "border-bottom: 1px solid black;"}">
                      ${point.series.name}
                    </span>:
                  </td>
                  <td style="text-align: right; font-weight: 600;">
                    <span style="${isOB && "border-bottom: 1px solid black;"}">
                      <span style="font-weight: 400; color: 'light-gray';">${
                        point.point.prefix || valuePrefix
                      }</span>
                      ${Highcharts.numberFormat(point.y, valueDecimalPoint)}
                      <span style="font-weight: 400; color: 'light-gray';">${
                        point.point.postfix || valuePostfix
                      }</span>
                    </span>
                  </td>

                  <td style="text-align: right; color: gray;">
                    ${point.point.d1Dist}
                  </td>

                  <td style="text-align: right; ${
                    point.point.distRatio <= -5 && "color: red;"
                  }">
                    (${point.point.distRatio}%)
                  </td>
                </tr>
              `;
            })
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

const AnatomyCstPriceSectionWrapper = React.memo((props) => {
  const filterContext = useAnatomyFilter();
  const dataContext = useAnatomyData();
  const uiContext = useAnatomyUI();

  // If Context providers aren't available yet (e.g. testing), fallback to props
  if (!filterContext || !dataContext || !uiContext) {
    return <AnatomyCstPriceSection {...props} />;
  }

  return (
    <AnatomyCstPriceSection
      {...props}
      timezoneType={filterContext.timezoneType}
      demandTypes={filterContext.demandTypes}
      dateRangeNum={filterContext.dateRangeNum}
      aggregation={filterContext.aggregation}
      isIncludeGhostImp={filterContext.isIncludeGhostImp}
      noPbType={filterContext.noPbType}
      selectedGroupTypes={filterContext.selectedGroupTypes}
      includeToday={filterContext.includeToday}
      data={dataContext.segmentData}
      uprSegmentId={dataContext.selectedUprSegmentId}
      st={uiContext.st}
      et={uiContext.et}
      handleSetExtremes={uiContext.handleSetExtremes}
    />
  );
});

export default AnatomyCstPriceSectionWrapper;
